import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthMarketplaceTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { GrowthAdminService, GrowthAgentInstallResponse } from './growth-admin.service';
import { GROWTH_AGENT_INSTALL_LIFECYCLE_STATUSES } from './growth-agent-marketplace-form.models';

@Component({
  standalone: true,
  selector: 'ain-growth-agent-marketplace-installs',
  imports: [AinPageComponent, InfoHintComponent, TableModule, FormsModule, ButtonModule, InputTextModule, SelectModule, RouterLink],
  template: `
    <ain-page [title]="t('growth.agentMarketplace.installs.title')" [description]="t('growth.agentMarketplace.installs.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.agentMarketplace.installs.help.title'"
        [descriptionKey]="'growth.agentMarketplace.installs.help.description'"
        [bulletKeys]="['growth.agentMarketplace.installs.help.bullets.permissions', 'growth.agentMarketplace.installs.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <input
            pInputText
            type="text"
            [(ngModel)]="searchTerm"
            [placeholder]="t('growth.agentMarketplace.installs.search')" />
          <p-select
            [options]="lifecycleOptions()"
            [(ngModel)]="lifecycleFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('growth.agentMarketplace.installs.filterLifecycle')" />
          <button pButton type="button" [label]="t('growth.agentMarketplace.installs.search')" (click)="applyFilters()"></button>
          <button pButton type="button" [label]="t('growth.agentMarketplace.installs.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button
            pButton
            type="button"
            [label]="t('growth.agentMarketplace.installs.actions.openSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openSelectedDetail()"></button>
          @if (canDisableSelected()) {
            <button
              pButton
              type="button"
              severity="danger"
              [label]="t('growth.agentMarketplace.installs.disable')"
              [loading]="actionId() === selected()?.id && actionType() === 'disable'"
              (click)="disableSelected()"></button>
          }
          <a class="link-btn" routerLink="/growth/marketplace/listings">{{ t('growth.agentMarketplace.installs.openListings') }}</a>
        </div>
        <p-table
          [value]="filteredRows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.agentMarketplace.installs.columns.listingKey') }}</th>
              <th>{{ t('growth.agentMarketplace.installs.columns.displayName') }}</th>
              <th>{{ t('growth.agentMarketplace.installs.columns.version') }}</th>
              <th>{{ t('growth.agentMarketplace.installs.columns.industryPackKey') }}</th>
              <th>{{ t('growth.agentMarketplace.installs.columns.lifecycleStatus') }}</th>
              <th>{{ t('growth.agentMarketplace.installs.columns.killSwitchEnabled') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.listingKey }}</button>
              </td>
              <td>{{ row.displayName }}</td>
              <td>{{ row.installedVersion ?? row.version }}</td>
              <td>{{ row.industryPackKey ?? '-' }}</td>
              <td>{{ lifecycleLabel(row.lifecycleStatus) }}</td>
              <td>{{ row.killSwitchEnabled ? t('common.yes') : t('common.no') }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-decoration: none; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
  `]
})
export class GrowthAgentMarketplaceInstallsComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly rows = signal<GrowthAgentInstallResponse[]>([]);
  readonly filteredRows = signal<GrowthAgentInstallResponse[]>([]);
  readonly selected = signal<GrowthAgentInstallResponse | null>(null);
  readonly errorMessage = signal('');
  readonly actionId = signal<string | null>(null);
  readonly actionType = signal<'disable' | null>(null);

  searchTerm = '';
  lifecycleFilter: string | null = null;

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthMarketplaceChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load();
  }

  lifecycleOptions(): Array<{ label: string; value: string }> {
    return GROWTH_AGENT_INSTALL_LIFECYCLE_STATUSES.map(value => ({
      value,
      label: this.lifecycleLabel(value)
    }));
  }

  lifecycleLabel(value: string): string {
    return this.t(`growth.agentMarketplace.lifecycle.${value}`);
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listAgentMarketplaceInstalls().subscribe({
      next: data => {
        this.rows.set(data);
        this.applyFilters();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.agentMarketplace.installs.messages.loadFailed'));
      }
    });
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    let filtered = this.rows();
    if (this.lifecycleFilter) {
      filtered = filtered.filter(item => item.lifecycleStatus === this.lifecycleFilter);
    }
    if (term) {
      filtered = filtered.filter(item =>
        item.listingKey.toLowerCase().includes(term)
        || item.displayName.toLowerCase().includes(term)
        || (item.industryPackKey ?? '').toLowerCase().includes(term));
    }
    this.filteredRows.set(filtered);
    const currentId = this.selected()?.id;
    this.selected.set(filtered.find(item => item.id === currentId) ?? filtered[0] ?? null);
  }

  onSelect(row: GrowthAgentInstallResponse | null): void {
    this.selected.set(row);
  }

  canDisableSelected(): boolean {
    return this.selected()?.lifecycleStatus === 'Installed';
  }

  openSelectedDetail(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.id);
  }

  openDetail(installId: string): void {
    void this.router.navigate(['/growth/marketplace/agent-installs', installId]);
  }

  disableSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.actionId.set(row.id);
    this.actionType.set('disable');
    this.errorMessage.set('');
    this.api.disableAgentMarketplaceInstall(row.id).pipe(finalize(() => {
      this.actionId.set(null);
      this.actionType.set(null);
    })).subscribe({
      next: updated => {
        this.rows.update(items => items.map(item => item.id === updated.id ? updated : item));
        this.applyFilters();
      },
      error: () => this.errorMessage.set(this.t('growth.agentMarketplace.installs.messages.disableFailed'))
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthMarketplaceTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
